import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button, Edit03, Menu, MenuItem, Popper, Spinner, Trash, useModal } from '@dashboard/ui'
import { CardTemplateRead, CardType } from '@/api/dashboard'
import { Page, PageHeader, Table, TableCell, TableRow } from '@/ui'
import { useGenerateCompanyPath } from '@/libs'
import { cardTemplatesQueryOpts } from '../api'
import { CardTemplateCreateModal, CardTemplateDeleteModal, CardTemplateEditModal } from '../components'

export const HubCardTemplatesPage = () => {
  const { t } = useTranslation()
  const { t: ct } = useTranslation('card-templates')
  const { companyId, gameId } = useGenerateCompanyPath()
  const { data: cardTemplates = [], isLoading } = useQuery(cardTemplatesQueryOpts(companyId, gameId))

  const openCreateModal = useModal<{ type: CardType }>(props => <CardTemplateCreateModal {...props} />)
  const openDeleteModal = useModal<{ cardTemplate: CardTemplateRead }>(props => <CardTemplateDeleteModal {...props} />)
  const openEditModal = useModal<{ cardTemplate: CardTemplateRead }>(props => <CardTemplateEditModal {...props} />)

  const availableCardTemplates = Object.values(CardType).filter(type => {
    return cardTemplates.every(template => template.card_type !== type)
  })

  if (isLoading) {
    return (
      <Page className="flex h-full items-center justify-center">
        <Spinner />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        extra={
          <Popper
            popper={props => (
              <Menu {...props} className="!w-[220px]">
                {availableCardTemplates.map(type => (
                  <MenuItem
                    key={type}
                    disabled={['reward', 'loyalty', 'popup'].includes(type)}
                    onClick={() => {
                      openCreateModal({ type })
                      props.onClose()
                    }}
                  >
                    <span>{type}</span>
                  </MenuItem>
                ))}
              </Menu>
            )}
            params={{ placement: 'bottom-end' }}
          >
            <Button>{ct('create')}</Button>
          </Popper>
        }
      >
        {ct('title')}
      </PageHeader>

      {!!cardTemplates.length && (
        <Table>
          <TableRow variant="header">
            <TableCell width="50%">{t('ID')}</TableCell>
            <TableCell width="100%">{t('Card Type')}</TableCell>
            <TableCell width="20%" />
          </TableRow>

          {cardTemplates
            .sort((a, b) => a.card_type.localeCompare(b.card_type))
            .map(template => (
              <TableRow key={template.id}>
                <TableCell width="50%">{template.id}</TableCell>
                <TableCell width="100%">{template.card_type}</TableCell>
                <TableCell width="20%" className="flex justify-end gap-2">
                  <Button variant="outline" color="secondary" onClick={() => openEditModal({ cardTemplate: template })}>
                    <Edit03 />
                    <span>Edit</span>
                  </Button>
                  <Button
                    className="w-8 p-0"
                    variant="outline"
                    color="secondary"
                    onClick={() => openDeleteModal({ cardTemplate: template })}
                  >
                    <Trash />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
        </Table>
      )}
    </Page>
  )
}
